<ng-container *transloco="let t">
    <div
        *ngIf="data && queries.length > 0"
        [dir]="lang === 'ar' ? 'rtl' : 'ltr'"
        class="report-data-table-dialog w-full max-w-full px-8 py-4"
        [class.report-data-table-dialog--compact]="!!data.compact"
    >
        <div class="report-data-table-bar w-full flex justify-end mb-6 sm:mb-1">
            <button
                type="button"
                mat-flat-button
                class="px-6 py-2 rounded-lg bg-primary"
                (click)="exportAsCsv()"
            >
                {{ t("Export as Excel") }}
            </button>
            <!-- Compact hosts (the messenger) have no dashboard chrome around the dialog, so it carries its own close. -->
            <button
                *ngIf="data.compact"
                type="button"
                mat-icon-button
                mat-dialog-close
                class="report-data-table-close"
                [aria-label]="t('Close')"
            >
                <mat-icon svgIcon="heroicons_outline:x-mark"></mat-icon>
            </button>
        </div>

        <div class="slider-container">
            <div
                class="slider-controls my-3 flex items-center"
                *ngIf="queries.length > 1"
                [attr.dir]="lang === 'ar' ? 'rtl' : 'ltr'"
            >
                <button
                    mat-icon-button
                    (click)="prevSlide()"
                    [disabled]="currentIndex === 0"
                >
                    <mat-icon> chevron_left </mat-icon>
                </button>

                <span class="mx-2 font-medium">
                    {{ currentIndex + 1 }} / {{ queries.length }}
                </span>

                <button
                    mat-icon-button
                    (click)="nextSlide()"
                    [disabled]="currentIndex === queries.length - 1"
                >
                    <mat-icon> chevron_right </mat-icon>
                </button>
            </div>

            <div class="slider-content" *ngIf="queries.length > 0">
                <div
                    class="slide"
                    *ngFor="let query of queries; let i = index"
                    [class.active]="i === currentIndex"
                >
                    <h1
                        class="report-data-table-heading text-center text-3xl font-semibold text-gray-600 dark:text-gray-300 mb-2"
                    >
                        {{ slideHeading(query) }}
                    </h1>
                    <p
                        *ngIf="query.tableName"
                        class="report-data-table-meta text-start text-lg font-normal text-gray-500 dark:text-gray-400 leading-4 mb-2"
                    >
                        <span
                            class="text-xl font-semibold text-gray-600 dark:text-gray-300 leading-4"
                            >{{ t("Source Table: ") }}
                        </span>
                        {{ query.tableName }}
                    </p>
                    <p
                        *ngIf="query.query"
                        class="report-data-table-meta text-start text-lg font-normal text-gray-500 dark:text-gray-400 leading-6 mb-2"
                    >
                        <span
                            class="text-xl font-semibold text-gray-600 dark:text-gray-300 leading-6"
                            >{{ t("SQL Query: ") }}
                        </span>
                        <ng-container *ngIf="isQueryLong(query.query)">
                            <ng-container
                                *ngIf="isQueryExpanded(i); else truncatedQuery"
                            >
                                {{ query.query }}
                                <button
                                    type="button"
                                    (click)="toggleQueryExpanded(i)"
                                    class="text-primary cursor-pointer ml-1 underline hover:no-underline"
                                >
                                    {{ t("see less") }}
                                </button>
                            </ng-container>
                            <ng-template #truncatedQuery>
                                {{ query.query | truncate: queryPreviewLimit }}
                                <button
                                    type="button"
                                    (click)="toggleQueryExpanded(i)"
                                    class="text-primary cursor-pointer ml-1 underline hover:no-underline"
                                >
                                    {{ t("see full query") }}
                                </button>
                            </ng-template>
                        </ng-container>
                        <ng-container *ngIf="!isQueryLong(query.query)">
                            {{ query.query }}
                        </ng-container>
                    </p>

                    <div
                        class="report-data-table-shell w-full bg-white dark:bg-transparent rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
                    >
                        <div class="table-container">
                            <table
                                *ngIf="query.data?.length"
                                class="report-data-table w-full"
                            >
                                <thead>
                                    <tr>
                                        <th
                                            *ngFor="
                                                let key of getKeysForQuery(query)
                                            "
                                            class="px-6 py-4 text-left text-sm font-bold"
                                        >
                                            <span class="flex items-center gap-2">
                                                <span>{{ key }}</span>
                                                <span
                                                    class="sort-icons inline-flex gap-1"
                                                    *ngIf="
                                                        shouldShowSortIcons(
                                                            key,
                                                            i
                                                        )
                                                    "
                                                >
                                                    <button
                                                        mat-icon-button
                                                        (click)="
                                                            sortData(
                                                                key,
                                                                'asc'
                                                            )
                                                        "
                                                        class="sort-icon w-6 h-6 text-gray-500 dark:text-gray-400 hover:text-primary transition-colors duration-200"
                                                        [attr.aria-label]="t('Sort Ascending')"
                                                    >
                                                        <mat-icon class="text-sm"
                                                            >arrow_upward</mat-icon
                                                        >
                                                    </button>
                                                    <button
                                                        mat-icon-button
                                                        (click)="
                                                            sortData(
                                                                key,
                                                                'desc'
                                                            )
                                                        "
                                                        class="sort-icon w-6 h-6 text-gray-500 dark:text-gray-400 hover:text-primary transition-colors duration-200"
                                                        [attr.aria-label]="t('Sort Descending')"
                                                    >
                                                        <mat-icon class="text-sm"
                                                            >arrow_downward</mat-icon
                                                        >
                                                    </button>
                                                </span>
                                            </span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        *ngFor="let item of query.data"
                                    >
                                        <td
                                            *ngFor="
                                                let key of getKeysForQuery(query)
                                            "
                                            [appCopyOnDblclick]="cell"
                                            class="copy-cell px-6 py-4 text-sm"
                                            [matTooltip]="
                                                t('Double-click to copy')
                                            "
                                            [matTooltipDisabled]="!!data.compact"
                                            matTooltipPosition="above"
                                        >
                                            {{
                                                item[key] | formatting : key
                                            }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <div
                                *ngIf="!query.data?.length"
                                class="px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400"
                            >
                                {{ t("No data available") }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</ng-container>
